<template>
<div>
  <div class="px-4 sm:px-0 mb-4">
    <PageTitle
      :title="$gettext('Tableau de bord')"
      :description="$gettext('Voici un résumé des données globales.')"/>
  </div>

  <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-2 mb-4">
    <div class="px-4 py-5 sm:px-6">
      <StatisticsOverview />
    </div>
  </div>

  <div class="grid grid-cols-3">
    <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-2 mr-4">
      <div class="px-4 py-5 sm:px-6">
        <span class="text-m font-semibold text-gray-900">{{ $gettext("Clients récents") }}</span>
      </div>
      <div class="px-4 py-5 sm:p-6">
        <table class="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">{{ $gettext("Nom") }}</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $gettext("Profil de risque") }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="customer in customerStore.customers.slice(0,4)" :key="customer.id">
              <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 flex items-center gap-2">
                <CustomerLink :customer="customer" />
              </td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <RiskScoringLabel :level="customer.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1">
      <div class="px-4 py-5 sm:px-6">
        <span class="text-m font-semibold text-gray-900">{{ $gettext("Répartition des risques de la clientèle") }}</span>
      </div>
      <div class="px-4 py-4 sm:px-6">
        <v-chart class="chart h-40 p-0 m-0" :option="option" autoresize />
        <div class="flex justify-between items-center mb-3">
          <span class="text-m font-normal text-gray-400">{{ $gettext("Clients à risque faible") }}</span>
          <span class="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-bold">{{ scoringOverview.countForRiskLevel("low") }}</span>
        </div>
        <div class="flex justify-between items-center mb-3">
          <span class="text-m font-normal text-gray-400">{{ $gettext("Clients à risque standard") }}</span>
          <span class="bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-sm font-bold">{{ scoringOverview.countForRiskLevel("medium") }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-m font-normal text-gray-400">{{ $gettext("Clients à risque élevé") }}</span>
          <span class="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-bold">{{ scoringOverview.countForRiskLevel("high") }}</span>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script setup>
import CustomerLink from "@/components/customers/CustomerLink.vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart from "vue-echarts";
import { ref, watchEffect, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import { useCustomerStore } from "@/stores/customer.js";
import { useScoringOverviewStore } from "@/stores/scoringOverview.js";
import PageTitle from "@/components/layout/PageTitle.vue";
import StatisticsOverview from "./stats/StatisticsOverview.vue";
use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const customerStore = useCustomerStore();
const scoringOverview = useScoringOverviewStore();
const route = useRoute();
const customerDashboardIncludes = ["lastRiskScoringLevel", "saleListingAgreementsCount", "rentalListingAgreementsCount"];

const option = ref({
  color: ["#00cc66", "#faa300", "#fc2f00"],
  series: [
    {
      type: "pie",
      radius: ["45%", "65%"],
      center: ["50%", "65%"],
      top: "-90%",
      bottom: "-90%",
      padAngle: 1,
      startAngle: 180,
      endAngle: 360,
      labelLine: {
        show: false
      },
      label: {
        show: false,
        position: "center"
      },
      data: []
    }
  ]
});

watchEffect(() => {
  if (scoringOverview.overview.length) {
    option.value.series[0].data = [
      scoringOverview.countForRiskLevel("low"),
      scoringOverview.countForRiskLevel("medium"),
      scoringOverview.countForRiskLevel("high")
    ]
    ;
  }
});

const refreshRiskOverview = async () => {
  await scoringOverview.fetchRiskOverview();
};

onMounted(async () => {
  await customerStore.fetchAll(null, customerDashboardIncludes);
  await customerStore.watchSourceChanges(() => {
    customerStore.fetchAll(null, customerDashboardIncludes);
  });
  await scoringOverview.watchResourceChanges(() => {
    scoringOverview.fetchRiskOverview();
  });
});

watch(route, refreshRiskOverview, { immediate: true });
</script>
